import api from './api';
import { CashFlowStatement } from '../components/dashboards/CashFlowProformaTable';

export type ExpenseBasis =
  | 'FIXED_MONTHLY'
  | 'FIXED_ANNUAL'
  | 'PERCENT_RENT'
  | 'PER_UNIT_MONTHLY'
  | 'PERCENT_PROPERTY_VALUE'
  | 'PERCENT_PURCHASE_PRICE';

export interface ExpenseInput {
  value: number;
  basis: ExpenseBasis;
  source?: string;
}

export type LoanType = 'FULLY_AMORTIZING' | 'INTEREST_ONLY';

export interface ScenarioInputs {
  purchasePrice: number;
  monthlyRent: number;
  monthlyOtherIncome?: number;
  units?: number;
  expenses?: Record<string, ExpenseInput>;
  customExpenses?: Array<ExpenseInput & { id?: string; label: string }>;
  immediateRepairs?: number;
  closingCosts?: ExpenseInput;
  otherAcquisitionCosts?: number;
  primaryFinancing?: {
    downPayment: ExpenseInput;
    interestRate: number;
    termYears: number;
    loanType?: LoanType | string;
  };
  secondaryFinancing?: {
    enabled?: boolean;
    label?: string;
    loanAmount: ExpenseInput;
    interestRate: number;
    termYears: number;
    loanType?: LoanType | string;
  };
}

export interface ScenarioMetrics {
  grossYield: number | null;
  capRate: number | null;
  noiAnnual: number;
  noiMonthly: number;
  cashFlowAnnual: number;
  cashFlowMonthly: number;
  cashOnCashReturn: number | null;
  dscr: number | null;
  breakEvenRatio: number | null;
  cashRequired: number;
  pctVsWti: number | null;
  annualRent: number;
  operatingExpensesAnnual: number;
  acquisitionCosts: number;
}

export interface OfferScenario {
  id: string;
  playgroundId: string;
  name: string;
  scenarioType: 'baseline' | 'editable';
  sortOrder: number;
  isBaseline: boolean;
  inputs: ScenarioInputs;
  results: ScenarioMetrics | null;
  cashFlowStatement?: CashFlowStatement | null;
}

export interface OfferPlayground {
  id: string;
  reportId: string;
  name: string;
  calculatorVersion: string;
  address: string | null;
  wtiEstimatedValue: number | null;
  wtiEstimatedRent: number | null;
  propertyType: string | null;
  propertyTypeLabel: string | null;
  units: number | null;
  scenarios: OfferScenario[];
  createdAt: string;
  updatedAt: string;
  lastOpenedAt: string;
}

export interface OfferPlaygroundListItem {
  id: string;
  reportId: string;
  name: string;
  address: string;
  propertyType: string;
  wtiEstimatedValue: number | null;
  selectedOfferPrice: number | null;
  scenarioCount: number;
  updatedAt: string;
  lastOpenedAt: string;
}

export const offerPlaygroundService = {
  async openForReport(reportId: string): Promise<OfferPlayground> {
    const res = await api.post<OfferPlayground>(`/reports/${reportId}/offer-playground`);
    return res.data;
  },

  async get(playgroundId: string): Promise<OfferPlayground> {
    const res = await api.get<OfferPlayground>(`/offer-playgrounds/${playgroundId}`);
    return res.data;
  },

  async list(): Promise<OfferPlaygroundListItem[]> {
    const res = await api.get<{ items: OfferPlaygroundListItem[] }>('/offer-playgrounds');
    return res.data.items || [];
  },

  async renamePlayground(playgroundId: string, name: string): Promise<OfferPlayground> {
    const res = await api.patch<OfferPlayground>(`/offer-playgrounds/${playgroundId}`, { name });
    return res.data;
  },

  async deletePlayground(playgroundId: string): Promise<void> {
    await api.delete(`/offer-playgrounds/${playgroundId}`);
  },

  async duplicatePlayground(playgroundId: string): Promise<OfferPlayground> {
    const res = await api.post<OfferPlayground>(`/offer-playgrounds/${playgroundId}/duplicate`);
    return res.data;
  },

  async addScenario(playgroundId: string, name?: string): Promise<OfferScenario> {
    const res = await api.post<OfferScenario>(`/offer-playgrounds/${playgroundId}/scenarios`, { name });
    return res.data;
  },

  async updateScenario(scenarioId: string, patch: { name?: string; inputs?: Partial<ScenarioInputs>; inputsJson?: ScenarioInputs }): Promise<OfferScenario> {
    const res = await api.patch<OfferScenario>(`/offer-scenarios/${scenarioId}`, patch);
    return res.data;
  },

  async resetScenario(scenarioId: string): Promise<OfferScenario> {
    const res = await api.post<OfferScenario>(`/offer-scenarios/${scenarioId}/reset`);
    return res.data;
  },

  async deleteScenario(scenarioId: string): Promise<void> {
    await api.delete(`/offer-scenarios/${scenarioId}`);
  },

  async duplicateScenario(scenarioId: string): Promise<OfferScenario> {
    const res = await api.post<OfferScenario>(`/offer-scenarios/${scenarioId}/duplicate`);
    return res.data;
  },

  async compare(playgroundId: string, scenarioIds: string[]): Promise<OfferScenario[]> {
    const res = await api.post<{ scenarios: OfferScenario[] }>(
      `/offer-playgrounds/${playgroundId}/compare`,
      { scenarioIds },
    );
    return res.data.scenarios;
  },
};
